import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { PublicLayout } from '@/components/public/PublicLayout';
import { SilaHero } from '@/components/public/SilaHero';
import { ContactsView } from '@/components/public/ContactsView';
import { Reveal, RevealGroup, motion, fadeUp } from '@/components/public/anim';
import { Seo } from '@/components/Seo';
import { Calendar, Eye, ArrowRight, Newspaper, Tag, LayoutGrid } from 'lucide-react';

const FALLBACK_IMG = 'https://images.pexels.com/photos/33343202/pexels-photo-33343202.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
// Immagine hero (riuso di un'immagine gia' presente nel set del sito, nessuna nuova risorsa)
const HERO_IMG = 'https://images.pexels.com/photos/33343202/pexels-photo-33343202.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920';

export function fmtDate(d, lang) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString(lang === 'en' ? 'en-GB' : 'it-IT', { day: '2-digit', month: 'short', year: 'numeric' }); } catch (e) { return ''; }
}

export default function Blog() {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState(null);
  const [cat, setCat] = useState('all');
  const [contactInfo, setContactInfo] = useState({});

  useEffect(() => {
    api.get('/blog/public/posts').then(({ data }) => setPosts(Array.isArray(data) ? data : [])).catch(() => setPosts([]));
    api.get('/cms/contacts').then(({ data }) => setContactInfo(data?.data || {})).catch(() => {});
  }, []);

  const cats = useMemo(() => {
    if (!posts) return [];
    const m = new Map();
    posts.forEach((p) => {
      const key = p.category?.slug || p.category?.name;
      if (key) {
        const cur = m.get(key) || { slug: key, name: p.category.name, count: 0 };
        cur.count += 1;
        m.set(key, cur);
      }
    });
    return Array.from(m.values());
  }, [posts]);

  const filtered = useMemo(() => {
    if (!posts) return [];
    if (cat === 'all') return posts;
    return posts.filter((p) => (p.category?.slug || p.category?.name) === cat);
  }, [posts, cat]);

  const featured = filtered[0] || null;
  const rest = featured ? filtered.slice(1) : filtered;

  const CatFilters = () => (
    <div className="rounded-[24px] border border-border bg-card p-5 shadow-[var(--shadow-sm)]" data-testid="blog-categories-panel">
      <h3 className="flex items-center gap-2 font-display text-lg font-semibold"><LayoutGrid className="h-4 w-4 text-primary" />{t('pages.blog.all') === 'Tutti' ? 'Categorie' : 'Categories'}</h3>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button onClick={() => setCat('all')} data-testid="blog-filter-all"
          className={`rounded-xl border px-3 py-2 text-left text-sm font-medium transition-colors ${cat === 'all' ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-accent'}`}>
          <span className="flex items-center gap-1.5"><Newspaper className="h-3.5 w-3.5" />{t('pages.blog.all')}</span>
          <span className="mt-0.5 block text-xs opacity-70">{(posts || []).length}</span>
        </button>
        {cats.map((c) => (
          <button key={c.slug} onClick={() => setCat(c.slug)} data-testid={`blog-filter-${c.slug}`}
            className={`rounded-xl border px-3 py-2 text-left text-sm font-medium transition-colors ${cat === c.slug ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-accent'}`}>
            <span className="flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" />{c.name}</span>
            <span className="mt-0.5 block text-xs opacity-70">{c.count} {c.count === 1 ? 'articolo' : 'articoli'}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <PublicLayout>
      <Seo title={t('pages.blog.title')} description={t('pages.blog.subtitle')} path="/blog" type="website" />

      {/* HERO con immagine (stessa forma delle altre pagine) */}
      <SilaHero image={HERO_IMG} badge={t('pages.blog.eyebrow')} title={t('pages.blog.title')} subtitle={t('pages.blog.subtitle')} icon={Newspaper} minH="52vh" testId="blog-hero" />

      <section className="sila-section pb-24 pt-10">
        {posts === null ? (
          <div className="grid gap-8 lg:grid-cols-12">
            <div className="space-y-4 lg:col-span-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}</div>
            <div className="lg:col-span-6"><Skeleton className="h-96 w-full rounded-[28px]" /></div>
            <div className="lg:col-span-3"><Skeleton className="h-64 w-full rounded-[24px]" /></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-9">
              <div className="rounded-[24px] border border-dashed border-border py-20 text-center" data-testid="blog-empty">
                <Newspaper className="mx-auto h-10 w-10 text-muted-foreground" />
                <p className="mt-3 text-muted-foreground">{t('pages.blog.empty')}</p>
              </div>
            </div>
            <div className="lg:col-span-3"><CatFilters /></div>
          </div>
        ) : (
          <div className="grid gap-8 lg:grid-cols-12">
            {/* SINISTRA: ultimi articoli */}
            <aside className="order-2 space-y-4 lg:order-1 lg:col-span-3" data-testid="blog-latest-column">
              <h3 className="font-display text-lg font-semibold">{i18n.language === 'en' ? 'Latest articles' : 'Ultimi articoli'}</h3>
              {rest.length === 0 ? (
                <p className="text-sm text-muted-foreground">{i18n.language === 'en' ? 'No more articles.' : 'Nessun altro articolo.'}</p>
              ) : (
                <RevealGroup className="space-y-3">
                  {rest.map((p) => (
                    <motion.div key={p.id} variants={fadeUp}>
                      <Link to={`/blog/${p.slug}`} className="group flex gap-3 rounded-xl border border-border bg-card p-2.5 transition-colors hover:bg-accent" data-testid={`blog-card-${p.slug}`}>
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-muted">
                          <img loading="lazy" src={p.cover_image || FALLBACK_IMG} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                        </div>
                        <div className="min-w-0">
                          {p.category?.name && <span className="text-[11px] font-medium uppercase tracking-wide text-primary">{p.category.name}</span>}
                          <p className="line-clamp-2 text-sm font-medium leading-snug transition-colors group-hover:text-primary">{p.title}</p>
                          <span className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Calendar className="h-3 w-3" />{fmtDate(p.published_at || p.created_at, i18n.language)}</span>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </RevealGroup>
              )}
            </aside>

            {/* CENTRO: articolo in evidenza */}
            <div className="order-1 lg:order-2 lg:col-span-6" data-testid="blog-featured-column">
              {featured && (
                <Reveal>
                  <Link to={`/blog/${featured.slug}`} className="group block overflow-hidden rounded-[28px] border border-border bg-card shadow-[var(--shadow-sm)] transition-shadow hover:shadow-[var(--shadow-md)]" data-testid="blog-featured">
                    <div className="relative h-72 overflow-hidden sm:h-80">
                      <img loading="lazy" src={featured.cover_image || FALLBACK_IMG} alt={featured.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <Badge className="absolute left-4 top-4 rounded-full bg-background/85 text-foreground backdrop-blur">{t('pages.blog.featured')}</Badge>
                    </div>
                    <div className="p-6 sm:p-8">
                      {featured.category?.name && <Badge variant="secondary" className="w-fit rounded-full">{featured.category.name}</Badge>}
                      <h2 className="mt-3 font-display text-2xl font-bold leading-tight sm:text-3xl">{featured.title}</h2>
                      <p className="mt-3 line-clamp-3 text-muted-foreground">{featured.excerpt}</p>
                      <div className="mt-5 flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" />{fmtDate(featured.published_at || featured.created_at, i18n.language)}</span>
                        <span className="flex items-center gap-1.5"><Eye className="h-4 w-4" />{featured.stats?.views || 0} {t('pages.blog.views')}</span>
                      </div>
                      <span className="mt-5 flex items-center gap-1.5 font-semibold text-primary transition-transform group-hover:translate-x-1">{t('pages.blog.readMore')} <ArrowRight className="h-4 w-4" /></span>
                    </div>
                  </Link>
                </Reveal>
              )}
            </div>

            {/* DESTRA: categorie */}
            <aside className="order-3 lg:order-3 lg:col-span-3" data-testid="blog-categories-column">
              <CatFilters />
            </aside>
          </div>
        )}
      </section>

      {/* Modulo Contatti aggregato nella pagina Blog — #14 */}
      <div className="border-t border-border bg-muted/30" data-testid="blog-contact-module">
        <ContactsView info={contactInfo} lang={i18n.language === 'en' ? 'en' : 'it'} embedded />
      </div>
    </PublicLayout>
  );
}
